import { boardCpModifier } from './programMeters';

// ---------------------------------------------------------------------------
// Coach Points (CP): the single currency for the head coach's time and influence.
//
// Earn:  a weekly allowance of 100 all year, +10 for a win and +20 for a playoff win. Unspent CP carries over:
//        a season brings about 2,800 + wins, so a talent takes most of a season of saving (balance review 2026-10-11).
// Spend: feeder events and prospect visits, college exposure (film, calls, camps), and permanent
//        coach talents in the skill tree.
// ---------------------------------------------------------------------------

export const WEEKLY_COACH_POINTS = 100;

/** Weekly allowance before talents: the same every week of the year. */
export function weeklyCoachPoints(_week: number): number {
  return WEEKLY_COACH_POINTS;
}

// Enough for a first spring of recruiting with a few assistants hired
export const STARTING_COACH_POINTS = 600;
export const WIN_CP_BONUS = 10;
export const PLAYOFF_WIN_CP_BONUS = 20;

export type TalentBranch = 'RECRUITER' | 'DEVELOPER' | 'POLITICIAN' | 'MANAGER';

export type TalentId =
  | 'RECRUITING_NETWORK'
  | 'COLLEGE_CONNECTIONS'
  | 'ASSISTANT_UPGRADE'
  | 'WEIGHT_ROOM_FANATIC'
  | 'BOARD_ROOM_SHIELD'
  | 'BOOSTER_BREAKFASTS'
  | 'BIGGER_BUDGET'
  | 'DEEP_POCKETS';

export interface CoachTalent {
  id: TalentId;
  branch: TalentBranch;
  name: string;
  description: string;
  cost: number; // CP: talents change core gameplay, so they take most of a season of saving
  requires?: TalentId; // the first talent in the branch
}

/** Every talent changes something real in the game (the effect helpers below are read by the systems). */
export const COACH_TALENTS: CoachTalent[] = [
  { id: 'RECRUITING_NETWORK', branch: 'RECRUITER', name: 'Recruiting Network', description: 'Prospect events cost 20% less.', cost: 2000 },
  {
    id: 'COLLEGE_CONNECTIONS',
    branch: 'RECRUITER',
    name: 'College Connections',
    description: 'Film, calls and camps for college prospects cost 40% less.',
    cost: 4000,
    requires: 'RECRUITING_NETWORK'
  },
  { id: 'ASSISTANT_UPGRADE', branch: 'DEVELOPER', name: 'Assistant Upgrade', description: 'Sharper assistants: +20% skill gain from every practice.', cost: 2000 },
  {
    id: 'WEIGHT_ROOM_FANATIC',
    branch: 'DEVELOPER',
    name: 'Weight Room Fanatic',
    description: 'Bigger off-season growth for every returning player.',
    cost: 4000,
    requires: 'ASSISTANT_UPGRADE'
  },
  { id: 'BOARD_ROOM_SHIELD', branch: 'POLITICIAN', name: 'Board Room Shield', description: 'Win over the school board: Rating up right away, and the board is more patient.', cost: 2000 },
  {
    id: 'BOOSTER_BREAKFASTS',
    branch: 'POLITICIAN',
    name: 'Booster Breakfasts',
    description: 'Win over the boosters: Rating up right away, and prospect recruiting gets a lift.',
    cost: 4000,
    requires: 'BOARD_ROOM_SHIELD'
  },
  { id: 'BIGGER_BUDGET', branch: 'MANAGER', name: 'Bigger Budget', description: '+₡10 every week.', cost: 2000 },
  { id: 'DEEP_POCKETS', branch: 'MANAGER', name: 'Deep Pockets', description: 'Win bonuses are doubled.', cost: 4000, requires: 'BIGGER_BUDGET' }
];

export const TALENT_BRANCH_LABELS: Record<TalentBranch, string> = {
  RECRUITER: 'Recruiter',
  DEVELOPER: 'Developer',
  POLITICIAN: 'Politician',
  MANAGER: 'Manager'
};

/** Why a talent can't be unlocked right now, or null if it can. */
export function talentBlocker(id: TalentId, owned: TalentId[], coachPoints: number): string | null {
  const talent = COACH_TALENTS.find((t) => t.id === id)!;
  if (owned.includes(id)) return 'Already unlocked';
  if (talent.requires && !owned.includes(talent.requires)) return `Unlock ${COACH_TALENTS.find((t) => t.id === talent.requires)!.name} first`;
  if (coachPoints < talent.cost) return `Needs ${formatCP(talent.cost)}`;
  return null;
}

// --- Effects (read by the systems they change) -------------------------------------------------

/** CP for display: the colon sign with thousands separators, e.g. ₡6,000. */
export const formatCP = (amount: number) => `₡${amount.toLocaleString('en-US')}`;
/** Bonus CP for a win (doubled by Deep Pockets). */
export const winBonus = (playoff: boolean, owned: TalentId[]) => (playoff ? PLAYOFF_WIN_CP_BONUS : WIN_CP_BONUS) * (owned.includes('DEEP_POCKETS') ? 2 : 1);
/** Weekly CP: the allowance, Bigger Budget, and the school board's support (see programMeters). */
export const weeklyCpIncome = (week: number, owned: TalentId[], boardTrust = 70) =>
  Math.max(0, weeklyCoachPoints(week) + (owned.includes('BIGGER_BUDGET') ? 10 : 0) + boardCpModifier(boardTrust));
/** A program's price: the Recruiting Network talent takes 20% off, the staff's Camp Directors their share. */
export const feederEventCost = (base: number, owned: TalentId[], staffDiscount = 0) =>
  Math.round(base * (owned.includes('RECRUITING_NETWORK') ? 0.8 : 1) * (1 - staffDiscount));
export const collegeActionCost = (base: number, owned: TalentId[]) => Math.max(1, Math.round(base * (owned.includes('COLLEGE_CONNECTIONS') ? 0.6 : 1)));
/** Strength-and-conditioning boost applied to the user's off-season progression. */
export const offseasonConditioningBonus = (owned: TalentId[]) => (owned.includes('WEIGHT_ROOM_FANATIC') ? 15 : 0);

